import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { layoutStore } from 'shared/layout'
import { IconButton } from 'shared/ui/IconButton'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { BreakpointsEnum } from 'shared/constants/breakpoints'
import { logger } from 'shared/lib/logger'
import { type IResponseAnalyze10DLCAISuggestion } from 'entities/Compliance/api/types/complianceType'
import { ComplianceApi } from 'entities/Compliance/api/compliance'
import styles from 'pages/settings/pages/compliance/ui/AiSidebar/styles.module.scss'
import { AISidebar } from 'pages/settings/pages/compliance/ui/AiSidebar/AISidebar'
import {
  siggestionStep,
  siggestionTitle,
} from 'pages/settings/pages/compliance/ui/AiSidebar/constants'
import { type ISidebarStatus } from './types'

import type { ComplianceLayerStore } from './complianceLayerStore'

export type ISiggestionListItem = IResponseAnalyze10DLCAISuggestion & {
  step: number
  title: string
}

export class AISidebarStore {
  private _isVisible = false
  private _isModalSidebar = false
  private _isShowHeaderStatus = false
  private _approvalChance: ISidebarStatus = 'medium'
  private _isAiCheckrWillUse = false
  private _isLoading = false
  private _isSuccessButton = false
  private _isErrorButton = false
  private _isContinueWithIssues = false
  private _siggestionList: ISiggestionListItem[] = []

  constructor(private _complianceLayerStore: ComplianceLayerStore) {
    makeAutoObservable(this)
  }

  get siggestionList() {
    return this._siggestionList
  }

  get complianceLayerStore() {
    return this._complianceLayerStore
  }

  get siggestionsByStep() {
    const groupedByStep = this.siggestionList.reduce((acc, item) => {
      if (!acc[item.step]) {
        acc[item.step] = []
      }
      acc[item.step].push(item)
      return acc
    }, {} as Record<number, ISiggestionListItem[]>)

    return groupedByStep
  }

  get isVisible() {
    return this._isVisible
  }

  get isShowHeaderStatus() {
    return this._isShowHeaderStatus
  }

  get isLoading() {
    return this._isLoading
  }

  get isSuccessButton() {
    return this._isSuccessButton
  }

  get isErrorButton() {
    return this._isErrorButton
  }

  get isContinueWithIssues() {
    return this._isContinueWithIssues
  }

  get approvalChance() {
    return this._approvalChance
  }

  get isAiCheckrWillUse() {
    return this._isAiCheckrWillUse
  }

  get isMobileView() {
    const width = document.documentElement.clientWidth
    return layoutStore.isViewXs || width === BreakpointsEnum.S
  }

  get isModalSidebar() {
    return this._isModalSidebar
  }

  setIsVisible = (isVisible: boolean) => {
    if (this.isMobileView) {
      this.setIsModalSidebar(isVisible)
      this.onMobilePreview()
    } else {
      this._isVisible = isVisible
    }
  }

  setIsModalSidebar = (isModalSidebar: boolean) => {
    this._isModalSidebar = isModalSidebar
  }

  setIsShowHeaderStatus = (isShowHeaderStatus: boolean) => {
    this._isShowHeaderStatus = isShowHeaderStatus
  }

  setApprovalChance = (siggestions: IResponseAnalyze10DLCAISuggestion[]) => {
    for (const suggestion of siggestions) {
      if (suggestion.severity.toLowerCase() === 'high') {
        this._approvalChance = 'low'
        break
      } else if (suggestion.severity.toLowerCase() === 'medium') {
        this._approvalChance = 'medium'
      } else {
        this._approvalChance = 'success'
      }
    }

    return this._approvalChance
  }

  setErrorApprovalChance = () => {
    this._approvalChance = 'error'
  }

  setIsAiCheckrWillUse = (isAiCheckrWillUse: boolean) => {
    this._isAiCheckrWillUse = isAiCheckrWillUse
  }

  setIsLoading = (isLoading: boolean) => {
    this._isLoading = isLoading
  }

  setIsSuccessButton = (isSuccessButton: boolean) => {
    this._isSuccessButton = isSuccessButton
  }

  setIsErrorButton = (isErrorButton: boolean) => {
    this._isErrorButton = isErrorButton
  }

  setIsContinueWithIssues = (isContinueWithIssues: boolean) => {
    this._isContinueWithIssues = isContinueWithIssues
  }

  isCurrentStepHasSuggestions = (step: number) => {
    return this.siggestionList.some((suggestion) => suggestion.step === step)
  }

  setSiggestionList = (siggestionList: IResponseAnalyze10DLCAISuggestion[]) => {
    this._siggestionList = siggestionList
      .filter((suggestion) => suggestion.severity.toLowerCase() !== 'low')
      .map((suggestion) => {
        const splitField = suggestion.field.split('.')
        const hasDot = splitField.length > 1
        const isOptInData = splitField[0] === 'optInData'

        const step = siggestionStep[splitField[0]] || 0
        const title = hasDot
          ? siggestionTitle[isOptInData ? splitField[2] : splitField[1]]
          : siggestionTitle[suggestion.field]

        return {
          ...suggestion,
          step,
          severity: suggestion.severity.toLowerCase() === 'high' ? 'low' : 'medium',
          title,
        }
      })
  }

  onMobilePreview = () => {
    const modalId = nanoid()

    const onCloseModal = () => {
      modalStore.closeModal(modalId)
      this.setIsModalSidebar(false)
      this.setIsShowHeaderStatus(true)
    }

    modalStore.addModal({
      id: modalId,
      type: 'right',
      contentStyle: {
        width: 380,
      },
      content: (
        <>
          <IconButton
            ariaLabel={'closeModalBtn'}
            icon={'close'}
            variant={'icon'}
            color={'secondary'}
            onClick={onCloseModal}
            className={styles.close}
          />
          <AISidebar sidebarStore={this} />
        </>
      ),
    })
  }

  handleResults = (approvalChance: ISidebarStatus, step?: number) => {
    return new Promise<boolean>((resolve) => {
      const isCurrentStepHasSuggestions = step ? this.isCurrentStepHasSuggestions(step) : true

      if (approvalChance === 'success' || !isCurrentStepHasSuggestions) {
        this.setIsSuccessButton(true)

        setTimeout(() => {
          this.setIsSuccessButton(false)
          this.setIsShowHeaderStatus(true)

          resolve(true)
        }, 2000)
      } else {
        this.setIsErrorButton(true)

        setTimeout(() => {
          this.setIsErrorButton(false)
          this.setIsVisible(true)
          this.setIsContinueWithIssues(true)
          resolve(false)
        }, 2000)
      }
    })
  }

  analyzeAI10DLC = async (step?: number) => {
    try {
      this.setIsLoading(true)
      const sampleMessagesData =
        this.complianceLayerStore?.complianceServiceCampaign?.data?.sampleMessagesData

      const { data } = await ComplianceApi.analyze10DLCAI({
        brand: {
          companyName:
            this.complianceLayerStore.organizationBusinessProfile?.businessInformation
              ?.business_name || '',
          website:
            step !== 1
              ? this.complianceLayerStore.organizationBusinessProfile?.businessInformation
                  ?.website_url || ''
              : undefined,
        },
        useCaseData:
          this.complianceLayerStore?.complianceServiceCampaign?.data?.useCaseData || null,
        sampleMessagesData: sampleMessagesData
          ? {
              sampleMessage1: sampleMessagesData.sampleMessage1,
              sampleMessage2: sampleMessagesData.sampleMessage2,
              includeAgeGatedContent: sampleMessagesData.includeAgeGatedContent,
              includeEmbeddedLinks: sampleMessagesData.includeEmbeddedLinks,
              includePhoneNumbers: sampleMessagesData.includePhoneNumbers,
              directLending: sampleMessagesData.directLending,
            }
          : null,
        optInData: this.complianceLayerStore?.complianceServiceCampaign?.data?.optInData || null,
        privacyPolicyUrl: sampleMessagesData?.privacyPolicyUrl || undefined,
        termsUrl: sampleMessagesData?.termsUrl || undefined,
      })

      const approvalChance = this.setApprovalChance(data.suggestions)
      this.setSiggestionList(data.suggestions)

      return this.handleResults(approvalChance, step)
    } catch (e) {
      logger.error(e)
      this.setErrorApprovalChance()
      this.setIsVisible(true)
      this.setIsContinueWithIssues(true)
      return false
    } finally {
      this.setIsLoading(false)
    }
  }
}
